import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Input, Button, Table, Thead, Tbody, Tr, Th, Td, FormControl, FormLabel, useToast, IconButton, Select, Tag, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Flex, useBreakpointValue
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';

const cores = [
  'teal', 'blue', 'red', 'orange', 'green', 'purple', 'pink', 'gray'
];

const Cartoes: React.FC = () => {
  const { user } = useAuth();
  const [cartoes, setCartoes] = useState<any[]>([]);
  const [nome, setNome] = useState('');
  const [diaFechamento, setDiaFechamento] = useState(1);
  const [diaVencimento, setDiaVencimento] = useState(10);
  const [limite, setLimite] = useState('');
  const [cor, setCor] = useState('teal');
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (!user) return;
    const fetchCartoes = async () => {
      setLoading(true);
      try {
        setCartoes(await api.get('/api/cartoes'));
      } catch {
        setCartoes([]);
      }
      setLoading(false);
    };
    fetchCartoes();
  }, [user]);

  const handleAdd = async () => {
    if (!nome) {
      toast({ title: 'Nome obrigatório', status: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const cartao = await api.post('/api/cartoes', {
        nome,
        dia_fechamento: diaFechamento,
        dia_vencimento: diaVencimento,
        limite: limite ? Number(limite) : null,
        cor,
      });
      setCartoes([...cartoes, cartao]);
      setNome('');
      setDiaFechamento(1);
      setDiaVencimento(10);
      setLimite('');
      setCor('teal');
      toast({ title: 'Cartão adicionado!', status: 'success' });
    } catch (err) {
      toast({ title: 'Erro ao adicionar', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  const startEdit = (cartao: any) => {
    setEditId(cartao.id);
    setEditData({ ...cartao });
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditData(null);
  };

  const handleEdit = async () => {
    if (!editData.nome) {
      toast({ title: 'Nome obrigatório', status: 'warning' });
      return;
    }
    setLoading(true);
    try {
      await api.put(`/api/cartoes/${editData.id}`, {
        nome: editData.nome,
        dia_fechamento: editData.dia_fechamento,
        dia_vencimento: editData.dia_vencimento,
        limite: editData.limite ? Number(editData.limite) : null,
        cor: editData.cor,
      });
      setCartoes(cartoes.map(c => c.id === editData.id ? { ...editData } : c));
      toast({ title: 'Cartão atualizado!', status: 'success' });
      cancelEdit();
    } catch (err) {
      toast({ title: 'Erro ao atualizar', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await api.delete(`/api/cartoes/${id}`);
      setCartoes(cartoes.filter(c => c.id !== id));
      toast({ title: 'Cartão excluído!', status: 'success' });
    } catch (err) {
      toast({ title: 'Erro ao excluir', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  return (
    <Box p={4} maxW="700px" mx="auto">
      <Heading size="lg" mb={4} textAlign="center">Cartões de Crédito</Heading>
      {isMobile ? (
        <Accordion allowToggle defaultIndex={[]} mb={8}>
          <AccordionItem borderRadius="md" boxShadow="md" bg="gray.50">
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="semibold">Novo cartão</Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <Flex gap={4} flexWrap="wrap" align="end">
                <FormControl>
                  <FormLabel>Nome</FormLabel>
                  <Input value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome do cartão" />
                </FormControl>
                <FormControl>
                  <FormLabel>Dia de fechamento</FormLabel>
                  <Input type="number" min={1} max={31} value={diaFechamento} onChange={e => setDiaFechamento(Number(e.target.value))} />
                </FormControl>
                <FormControl>
                  <FormLabel>Dia de vencimento</FormLabel>
                  <Input type="number" min={1} max={31} value={diaVencimento} onChange={e => setDiaVencimento(Number(e.target.value))} />
                </FormControl>
                <FormControl>
                  <FormLabel>Limite (opcional)</FormLabel>
                  <Input type="number" value={limite} onChange={e => setLimite(e.target.value)} placeholder="Limite do cartão" />
                </FormControl>
                <FormControl>
                  <FormLabel>Cor</FormLabel>
                  <Select value={cor} onChange={e => setCor(e.target.value)}>
                    {cores.map(c => (
                      <option value={c} key={c}>{c}</option>
                    ))}
                  </Select>
                </FormControl>
                <Button colorScheme="teal" mt={6} onClick={handleAdd} isLoading={loading}>Adicionar</Button>
              </Flex>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ) : (
        <Box mb={8} p={4} borderRadius="md" boxShadow="md" bg="gray.50">
          <Heading size="sm" mb={4}>Novo cartão</Heading>
          <Flex gap={4} flexWrap="wrap" align="end">
            <FormControl>
              <FormLabel>Nome</FormLabel>
              <Input value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome do cartão" />
            </FormControl>
            <FormControl>
              <FormLabel>Dia de fechamento</FormLabel>
              <Input type="number" min={1} max={31} value={diaFechamento} onChange={e => setDiaFechamento(Number(e.target.value))} />
            </FormControl>
            <FormControl>
              <FormLabel>Dia de vencimento</FormLabel>
              <Input type="number" min={1} max={31} value={diaVencimento} onChange={e => setDiaVencimento(Number(e.target.value))} />
            </FormControl>
            <FormControl>
              <FormLabel>Limite (opcional)</FormLabel>
              <Input type="number" value={limite} onChange={e => setLimite(e.target.value)} placeholder="Limite do cartão" />
            </FormControl>
            <FormControl>
              <FormLabel>Cor</FormLabel>
              <Select value={cor} onChange={e => setCor(e.target.value)}>
                {cores.map(c => (
                  <option value={c} key={c}>{c}</option>
                ))}
              </Select>
            </FormControl>
            <Button colorScheme="teal" mt={6} onClick={handleAdd} isLoading={loading}>Adicionar</Button>
          </Flex>
        </Box>
      )}
      <Box>
        <Heading size="sm" mb={4}>Seus cartões</Heading>
        <Box>
          <Box overflowX="auto" maxW="100%">
            {/* Tabela larga rola dentro da própria caixa: no celular a página inteira não desliza pro lado */}
            <Table size="sm" variant="simple" w="100%">
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th display={{ base: "none", md: "table-cell" }}>Fechamento</Th>
                  <Th display={{ base: "none", md: "table-cell" }}>Vencimento</Th>
                  <Th display={{ base: "none", md: "table-cell" }}>Limite</Th>
                  <Th display={{ base: "none", md: "table-cell" }}>Cor</Th>
                  <Th>Ações</Th>
                  <Th>Fatura</Th>
                </Tr>
              </Thead>
              <Tbody>
                {cartoes.map(cartao => (
                  <Tr key={cartao.id}>
                    {editId === cartao.id ? (
                      <>
                        <Td><Input value={editData.nome} onChange={e => setEditData({ ...editData, nome: e.target.value })} size="sm" /></Td>
                        <Td display={{ base: "none", md: "table-cell" }}><Input type="number" min={1} max={31} value={editData.dia_fechamento} onChange={e => setEditData({ ...editData, dia_fechamento: Number(e.target.value) })} size="sm" /></Td>
                        <Td display={{ base: "none", md: "table-cell" }}><Input type="number" min={1} max={31} value={editData.dia_vencimento} onChange={e => setEditData({ ...editData, dia_vencimento: Number(e.target.value) })} size="sm" /></Td>
                        <Td display={{ base: "none", md: "table-cell" }}><Input type="number" value={editData.limite || ''} onChange={e => setEditData({ ...editData, limite: e.target.value })} size="sm" /></Td>
                        <Td display={{ base: "none", md: "table-cell" }}><Select value={editData.cor} onChange={e => setEditData({ ...editData, cor: e.target.value })} size="sm">{cores.map(c => (<option value={c} key={c}>{c}</option>))}</Select></Td>
                        <Td>
                          <IconButton aria-label="Salvar" icon={<CheckIcon />} size="sm" colorScheme="teal" onClick={handleEdit} mr={2} />
                          <IconButton aria-label="Cancelar" icon={<CloseIcon />} size="sm" onClick={cancelEdit} />
                        </Td>
                        <Td></Td>
                      </>
                    ) : (
                      <>
                        <Td><Tag colorScheme={cartao.cor}>{cartao.nome}</Tag></Td>
                        <Td display={{ base: "none", md: "table-cell" }}>{cartao.dia_fechamento}</Td>
                        <Td display={{ base: "none", md: "table-cell" }}>{cartao.dia_vencimento}</Td>
                        <Td display={{ base: "none", md: "table-cell" }}>{cartao.limite ? Number(cartao.limite).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}</Td>
                        <Td display={{ base: "none", md: "table-cell" }}><Tag colorScheme={cartao.cor}>{cartao.cor}</Tag></Td>
                        <Td>
                          <IconButton aria-label="Editar" icon={<EditIcon />} size="sm" variant="ghost" onClick={() => startEdit(cartao)} mr={2} />
                          <IconButton aria-label="Excluir" icon={<DeleteIcon />} size="sm" colorScheme="red" variant="ghost" onClick={() => handleDelete(cartao.id)} />
                        </Td>
                        <Td>
                          <Button
                            size="sm"
                            colorScheme="teal"
                            variant="outline"
                            onClick={() => navigate(`/faturas-cartao?cartao_id=${cartao.id}`)}
                            width={{ base: '100%', md: 'auto' }}
                            px={2}
                            whiteSpace="nowrap"
                          >
                            Ver fatura
                          </Button>
                        </Td>
                      </>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Cartoes; 